import React, { useContext, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import GlobalContext from 'hooks/contexts/GlobalContext';
import Questionnaire from 'components/form-wizards/Questionnaire';
import ClickPortalSearchHeader from 'components/form-wizards/click-portal-search/ClickPortalSearchHeader';
import Wysiwyg from 'components/blocks/wysiwyg/Wysiwyg';
import ResponsiveImage from 'components/base/responsiveImage/ResponsiveImage';
import DefaultErrorBoundary from 'components/error-boundaries/DefaultErrorBoundary';
import ClickPortalResults from 'components/sections/lists/ClickPortalResults/ClickPortalResults';
import cx from 'utils/classnames';
import { AVAILABLE_COMPONENTS } from 'components/AVAILABLE_COMPONENTS';
import type { Questionnaire as QuestionnaireType } from 'types';
import { CLICK_PORTAL_CARD_LAYOUT } from 'consts';

const TRANSITION_DURATION = 300;

const PORTAL_SEARCH_SHELL_QUESTIONNAIRE_COMPONENTS = new Set<string>([
  AVAILABLE_COMPONENTS.CLICK_PORTAL_SIDEBAR_FORM_WIZARD,
  AVAILABLE_COMPONENTS.CLICK_PORTAL_FORM_WIZARD,
  AVAILABLE_COMPONENTS.MICRO_PORTAL_FORM_WIZARD,
]);

function isPortalSearchShellQuestionnaire(
  q: QuestionnaireType | null | undefined
): boolean {
  if (!q?.component) {
    return false;
  }
  return PORTAL_SEARCH_SHELL_QUESTIONNAIRE_COMPONENTS.has(q.component);
}

/**
 * Click portal search layout: sidebar questionnaire, step progress, then header + results.
 * Renders {@link ClickPortalResults}. The portal search page follows
 * `clickPortalSettings.isMultiRow`. A Portal Results vertical list passes
 * `isVerticalList` and can pass its own `cardLayout`.
 */
export default function ClickPortalSearchControlsAndResults({
  showSearchControls = true,
  searchControlsPosition = 'sidebar',
  showSearchHeader = true,
  cardLayout,
  isVerticalList = false,
}: {
  showSearchControls?: boolean;
  searchControlsPosition?: 'sidebar' | 'inline' | string;
  showSearchHeader?: boolean;
  cardLayout?: CLICK_PORTAL_CARD_LAYOUT;
  isVerticalList?: boolean;
} = {}): React.JSX.Element {
  const clickPortalSearchResultsRef = useRef<null | HTMLDivElement>(null);

  const {
    clickPortal: { shouldShowSteps, isLoading, firstRow },
    questionnaires,
    questionnaire,
    windowSize: { currentBreakpoint = 'mobile' },
    actions: { updateGlobalRefs },
    siteMeta: { clickPortalSettings },
  } = useContext(GlobalContext);

  const resolvedQuestionnaire =
    questionnaires?.clickPortal ??
    (isPortalSearchShellQuestionnaire(questionnaire)
      ? questionnaire
      : undefined);

  const { content, image } =
    questionnaires?.default?.sidebarContent ??
    questionnaires?.clickPortal?.sidebarContent ??
    resolvedQuestionnaire?.sidebarContent ??
    {};

  const searchControlsAreInline = searchControlsPosition === 'inline';
  const showProgressSteps = shouldShowSteps && !isVerticalList;
  const searchControls = showSearchControls ? (
    <div
      className={cx('clickPortalSearch__ctrls', {
        'clickPortalSearch__ctrls--inline': searchControlsAreInline,
      })}
    >
      {resolvedQuestionnaire && (
        <Questionnaire
          {...resolvedQuestionnaire}
          showSteps={!isVerticalList}
          disableTrustedForm
        />
      )}
    </div>
  ) : null;

  useEffect(() => {
    updateGlobalRefs({
      clickPortalSearchResultsRef,
    });
    return (): void => {
      updateGlobalRefs({
        clickPortalSearchResultsRef: null,
      });
    };
    // `updateGlobalRefs` from context is not referentially stable (new each render from getActions).
    // Ref object is stable; register once like the legacy portal/search page.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount/unmount only
  }, []);

  return (
    <div
      className={cx({
        clickPortalSearch: true,
        'clickPortalSearch--isLoading': isLoading,
        'clickPortalSearch--isMultiRow': clickPortalSettings?.isMultiRow,
      })}
    >
      {currentBreakpoint === 'tablet' &&
        showSearchHeader &&
        !showProgressSteps && (
          <div className="clickPortalSearch__header">
            <ClickPortalSearchHeader
              title={firstRow?.title}
              subTitle={firstRow?.subTitle}
              showTitle
            />
          </div>
        )}
      {!searchControlsAreInline && searchControls}
      <div className="clickPortalSearch__body">
        <CSSTransition
          in={showProgressSteps}
          className="clickPortalSearch__progress"
          classNames="clickPortalSearch__progress"
          timeout={TRANSITION_DURATION}
        >
          <div>
            {image && (
              <div className="clickPortalSearch__progressImg">
                <ResponsiveImage images={image} />
              </div>
            )}
            <div className="clickPortalSearch__progressContent">
              <Wysiwyg content={content} />
              <div
                className="clickPortalSearch__progressSteps"
                ref={clickPortalSearchResultsRef}
              />
            </div>
          </div>
        </CSSTransition>
        <CSSTransition
          in={!showProgressSteps}
          appear
          unmountOnExit
          className="clickPortalSearch__content"
          classNames="clickPortalSearch__content"
          timeout={500}
        >
          <div>
            {showSearchHeader && currentBreakpoint !== 'tablet' && (
              <div className="clickPortalSearch__header">
                <ClickPortalSearchHeader
                  title={firstRow?.title}
                  subTitle={firstRow?.subTitle}
                  showTitle={!showProgressSteps}
                />
              </div>
            )}
            {searchControlsAreInline && searchControls}
            <div className="clickPortalSearch__results">
              <DefaultErrorBoundary>
                <ClickPortalResults
                  cardLayout={cardLayout}
                  isVerticalList={isVerticalList}
                />
              </DefaultErrorBoundary>
            </div>
          </div>
        </CSSTransition>
      </div>
    </div>
  );
}
